import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowClockwise, GoogleDriveLogo, Microphone, Paperclip, Plus, Warning}
    from '../../icons';
import {AudioLevelMeter} from '../../audio/AudioLevelMeter';
import {AudioNotePlayer} from '../../audio/AudioNotePlayer';
import {useMemoryIngest, UPLOAD_STATUS} from './useMemoryIngest';
import {useSourcesDrawer} from './feed/sourcesDrawerContext';
import {FileKindIcon} from './feed/fileKindIcon';
import {INGEST_ACCEPT, INGEST_MODE} from '../memory/ingestFiles';
import {useAudioRecorder, STATUS_RECORDING, STATUS_REQUESTING, STOP_REASON_LIMIT, STOP_REASON_USER}
    from '../../../hooks/useAudioRecorder';
import {formatElapsedShort} from '../../../utils/audioRecording';
import {anchorAttr} from '../../../guides/anchors';

/**
 * La Barra Total: la puerta principal para alimentar la memoria de un cliente.
 *
 * Escribir, pegar un texto, pegar un enlace, soltar o elegir archivos (texto, PDF, Word, imagen,
 * audio), pegar una captura o grabar una nota de audio — todo entra por acá. Comparte la ingesta
 * con la zona de drop del orbe (`useMemoryIngest`): son la misma acción con dos gestos, y
 * duplicarla dejaría dos caminos que se desincronizan.
 *
 * **La tira de subidas** (`.iab-omni-uploads`) va como HERMANA de la caja, no adentro: el autogrow
 * del textarea mide `scrollHeight` y no se entera de lo que haya al lado, y la caja conserva su
 * alto de 36 px por control. Es el slot `.omniact` del mockup.
 *
 * **El micrófono** graba DENTRO de la misma caja (reemplaza el campo por la fila de grabación), no
 * en un panel aparte como el `#recorder` del mockup: mantiene la mirada donde se hizo el clic y los
 * controles miden 36 px, así que la caja no salta. Sin soporte de `MediaRecorder` el botón no se
 * dibuja — un botón ausente no promete nada.
 *
 * **Detener NO manda: deja la nota para escuchar** (Mandar / Regrabar / Descartar). Antes el botón
 * decía "Detener y mandar" y la nota se iba sin que nadie la oyera nunca; cuando el extractor la
 * rechazaba, no había forma de saber si el problema era el micrófono, lo que se dijo, o el sistema
 * (2026-09-04: dos notas de prueba se transcribieron bien y la pantalla dijo "material vacío").
 * El medidor de nivel resuelve la mitad de esa pregunta ANTES de grabar, y el reproductor la otra
 * mitad después.
 *
 * **El TIPO del material se declara acá** desde el 08/09 (card ScPxmmB3). Era lo único que el
 * compositor plegado —«Escribir, pegar o adjuntar», debajo del orbe— tenía y esta barra no, así que
 * al sacarlo (un segundo editor en una pantalla que ya tenía éste) bajó su selector. Es opcional y
 * arranca colapsado: la vía normal sigue siendo escribir y mandar, y quien llega desde una tarjeta
 * del instructivo se encuentra el tipo YA elegido y el placeholder de ese material.
 *
 * Debajo de la caja ya no va la lista de formatos (`omni.hint`): eran dos renglones de extensiones
 * imposibles de leer, en blanco y pegados al campo. Lo que no entra lo dice el error del momento,
 * que es cuando el dato sirve.
 */
// Alto máximo del campo antes de scrollear adentro, en líneas. Espeja el `max-height` del SCSS.
const MAX_ROWS = 6;

// La barra sin orientar: placeholder genérico. Desde la detección automática (23/10) ningún tipo
// viaja al backend —el material lo clasifica Dashbo—: el tipo que elige una tarjeta del instructivo
// sólo cambia el placeholder para decir qué pegar.
export const KIND_AUTO = 'auto';

// Placeholder propio por tipo: quien llega desde una tarjeta del instructivo necesita que el campo
// le diga qué pegar de ESE material — un placeholder genérico desperdicia el clic que acaba de dar.
// Las claves conservan el prefijo `composer_ph_` del compositor que las estrenó: el texto es el
// mismo y renombrar ocho claves en un `es.json` compartido no le cambia nada a nadie.
export const kindPlaceholderKey = (kind) => (kind && kind !== KIND_AUTO
    ? `ia_booster.feed.composer_ph_${kind}`
    : 'ia_booster.omni.placeholder');

// Tope de la nota de audio. Opus a ~48 kbps → 30 min ≈ 10 MB; el AAC de Safari (~128 kbps) llega
// al tope de bytes a los ~20 min, y ahí manda el tope de bytes (el copy del límite lo cubre). La
// red de bytes (19 MB) queda por debajo del cap de AUDIO del backend —25 MB, no los 20 MiB de los
// documentos— para que el rechazo nunca sea suyo: cortar acá permite ofrecer lo grabado hasta ahí.
export const MEMORY_AUDIO_MAX_SECONDS = 30 * 60;
export const MEMORY_AUDIO_MAX_BYTES = 19 * 1024 * 1024;
export const MEMORY_AUDIO_BITS = 48000;
export const MEMORY_AUDIO_NEAR_LIMIT_SECONDS = 60;

// Nombre para una imagen pegada desde el portapapeles, que suele venir como `image.png` o sin
// nombre. La extensión importa: la tabla de admisión clasifica por ella.
const pastedImageFile = (file) => {
    const ext = (String(file.type || '').split('/')[1] || 'png').replace('jpeg', 'jpg');
    const hasExt = /\.[a-z0-9]+$/i.test(file.name || '');
    if (hasExt) return file;
    return new File([file], `captura.${ext}`, {type: file.type});
};

const pasteStamp = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

export function BoosterOmnibar({
    clientId,
    ingest,
    onIngested,
    disabled = false,
    // El tipo que eligió una tarjeta del instructivo. Lo baja el contenedor del acto porque esa
    // tarjeta vive en OTRO componente, hermano de éste. No viaja al backend: sólo orienta el
    // placeholder, y `onKindChange(KIND_AUTO)` lo suelta cuando algo entró.
    kind = KIND_AUTO,
    onKindChange,
    // Sube cuando alguien pide la barra desde afuera (una tarjeta del instructivo): la trae a la
    // vista y enfoca el campo. Es un contador y no un booleano para que dos clics seguidos al mismo
    // tipo también enfoquen.
    focusToken = 0,
}) {
    const {t} = useTranslation();
    const [text, setText] = useState('');
    const [dragging, setDragging] = useState(false);
    const [multiline, setMultiline] = useState(false);
    const [pendingAudio, setPendingAudio] = useState(null);
    const fileRef = useRef(null);
    const areaRef = useRef(null);
    const micRef = useRef(null);
    const stopRef = useRef(null);
    // `dragleave` dispara al cruzar CADA hijo de la caja (los botones): sin contar la profundidad,
    // el resaltado parpadea mientras el usuario mueve el mouse adentro.
    const dragDepthRef = useRef(0);
    const wasSendingRef = useRef(false);
    const sendRef = useRef(null);
    // El escenario ya sostiene la ingesta (la comparte con el drop del orbe) y la baja por prop.
    // El hook local es el fallback para montar la barra sola — nunca corren los dos: el único
    // efecto de `useMemoryIngest` (`beforeunload`) sólo hace algo con subidas binarias en vuelo,
    // así que la instancia que no se usa no cuesta nada.
    const own = useMemoryIngest(clientId, onIngested);
    const active = ingest || own;
    const {
        submitText, ingestFile, ingestDrop, cancelUpload, dismissUpload, sending, uploads = [],
    } = active;
    // Un PDF subiendo NO congela la barra: los archivos viajan apenas se eligen y bloquear por
    // `sending` dejaba el campo y el micrófono muertos durante toda la subida. `sendingText`
    // sólo cuenta lo que sale por este mismo campo. (En el Composer el freeze sí corresponde:
    // ahí el submit es un gesto único que cierra el panel.)
    const sendingText = active.sendingText != null ? active.sendingText : sending;
    // Una ingesta bajada por prop puede ser vieja (sin `ingestFiles`): se cae al archivo por archivo.
    // Memoizado porque es dependencia de un `useCallback`: sin esto cambia en cada render y se
    // rehace el handler entero cada vez.
    const activeIngestFiles = active.ingestFiles;
    const ingestFiles = useMemo(
        () => activeIngestFiles
            || ((files, kind, opts) => Promise.all([...files].map(file => ingestFile(file, kind, opts)))),
        [activeIngestFiles, ingestFile]);
    // En el Recorrido, Drive se conecta desde el panel de Fuentes de esta misma página. Fuera de
    // él (el escenario por-ruta) sigue siendo el link de siempre.
    const drawer = useSourcesDrawer();
    const escape = {interpolation: {escapeValue: false}};

    const clearKind = useCallback(() => {
        if (onKindChange) onKindChange(KIND_AUTO);
    }, [onKindChange]);

    /**
     * Suelta el tipo elegido SÓLO si algo entró de verdad.
     *
     * Es la misma regla que el texto: si el envío rebota —un archivo demasiado grande, un formato
     * que no entra, el kill-switch apagado— el placeholder sigue diciendo qué pegar para el
     * reintento. Soltarlo sincrónicamente, apenas se dispara el envío, hacía justo lo contrario en
     * tres de las cuatro puertas (drop, captura pegada y selector de archivos).
     *
     * Las cuatro puertas devuelven cosas distintas y por eso el desenlace se normaliza acá:
     * `submitText` resuelve un booleano, `ingestFiles` un `[{item, ok}]`, e `ingestDrop` cualquiera
     * de los dos —o `false` si el drop no traía ni archivos ni texto, que es el caso donde antes se
     * limpiaba sin que hubiera pasado nada.
     */
    const clearKindIfEntered = useCallback((result) => {
        Promise.resolve(result).then((value) => {
            const entered = Array.isArray(value)
                ? value.some(item => item && item.ok)
                : value === true;
            if (entered) clearKind();
        });
    }, [clearKind]);

    // --- micrófono --- //
    const sendAudio = useCallback((audio) => {
        const file = new File([audio.blob], audio.filename, {type: audio.blob.type});
        ingestFile(file, undefined, {
            title: t('ia_booster.omni.audio_title', {time: formatElapsedShort(audio.durationSeconds * 1000)}),
            durationSeconds: audio.durationSeconds,
        });
    }, [ingestFile, t]);

    const recorderRef = useRef(null);

    /** Suelta el `objectURL` de una nota que ya no se va a escuchar. */
    const releasePreview = useCallback((entry) => {
        const url = entry && entry.audio && entry.audio.previewUrl;
        if (url && typeof URL !== 'undefined' && URL.revokeObjectURL) URL.revokeObjectURL(url);
    }, []);

    const onAudioReady = useCallback((audio, meta) => {
        const reason = (meta && meta.reason) || STOP_REASON_USER;
        if (recorderRef.current) recorderRef.current.reset();
        // NINGUNA nota se manda sola, la haya detenido el usuario o no. Es un audio: quien lo grabó
        // es el único que puede saber si dice lo que quería decir, y hasta que exista este paso
        // nadie lo escuchaba jamás — ni antes de mandarlo ni después.
        setPendingAudio({audio, reason});
    }, []);

    const recorder = useAudioRecorder({
        maxSeconds: MEMORY_AUDIO_MAX_SECONDS,
        maxBytes: MEMORY_AUDIO_MAX_BYTES,
        nearLimitSeconds: MEMORY_AUDIO_NEAR_LIMIT_SECONDS,
        bitsPerSecond: MEMORY_AUDIO_BITS,
        errorKeyPrefix: 'client_memory.audio',
        filenameBase: 'nota-de-audio',
        id: 'memory-audio',
        // La nota se escucha antes de mandarse, así que el `objectURL` es necesario. Su propiedad
        // es de este componente: se revoca al mandar, descartar, regrabar y en el unmount, porque
        // cada uno retiene su blob (hasta 19 MB) mientras viva.
        withPreviewUrl: true,
        // Las barras de nivel: lo único que prueba que el micrófono está captando.
        withMeter: true,
        onReady: onAudioReady,
    });
    recorderRef.current = recorder;
    const isRecording = recorder.status === STATUS_RECORDING;
    const isRequesting = recorder.status === STATUS_REQUESTING;
    const recActive = isRecording || isRequesting;

    const cancelRecording = useCallback(() => {
        recorder.cancel();
        if (micRef.current) micRef.current.focus();
    }, [recorder]);
    const sendPending = useCallback(() => {
        if (!pendingAudio) return;
        sendAudio(pendingAudio.audio);
        // El `File` que viaja se armó con el blob, no con la URL: revocarla acá no toca la subida.
        releasePreview(pendingAudio);
        setPendingAudio(null);
    }, [pendingAudio, releasePreview, sendAudio]);
    const discardPending = useCallback(() => {
        releasePreview(pendingAudio);
        setPendingAudio(null);
        if (micRef.current) micRef.current.focus();
    }, [pendingAudio, releasePreview]);
    const redoPending = useCallback(() => {
        releasePreview(pendingAudio);
        setPendingAudio(null);
        recorder.start();
    }, [pendingAudio, recorder, releasePreview]);

    // Foco: al empezar a grabar va a «Detener» (es la salida); al quedar la nota para revisar, a
    // «Mandar», que es lo que el usuario va a querer hacer casi siempre; al mandar, al campo.
    useEffect(() => {
        if (isRecording && stopRef.current) stopRef.current.focus();
    }, [isRecording]);

    useEffect(() => {
        if (pendingAudio && sendRef.current) sendRef.current.focus();
    }, [pendingAudio]);

    // Cerrar la pantalla con una nota sin mandar no puede dejar el blob retenido hasta el F5.
    // Sólo en el unmount: con `pendingAudio` en las dependencias revocaría la URL de la nota que
    // se está escuchando en ese momento.
    const pendingRef = useRef(null);
    pendingRef.current = pendingAudio;
    useEffect(() => () => releasePreview(pendingRef.current), [releasePreview]);

    // El campo crece con lo que se pega. Se mide sobre `scrollHeight` con el alto en 'auto': sin
    // ese reset previo el textarea nunca se achica al borrar texto, porque su `scrollHeight` no
    // baja por debajo del alto que ya tiene puesto.
    const autoGrow = useCallback(() => {
        const el = areaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const line = parseFloat(getComputedStyle(el).lineHeight) || 20;
        const rows = Math.min(Math.round(el.scrollHeight / line), MAX_ROWS);
        el.style.height = `${el.scrollHeight}px`;
        setMultiline(rows > 1);
    }, []);

    const send = useCallback(() => {
        if (!text.trim()) return;
        const result = submitText(text);
        // `submitText` resuelve `true` sólo si el documento entró: limpiar antes dejaría al
        // usuario sin lo que escribió cuando el backend rechaza el enlace o se corta la red (ahí
        // reenviar el mismo texto no lo duplica). El tipo elegido se suelta con el mismo criterio.
        if (result && typeof result.then === 'function') {
            result.then((ok) => {
                if (!ok) return;
                setText('');
                setMultiline(false);
                clearKind();
                if (areaRef.current) areaRef.current.style.height = 'auto';
            });
        }
    }, [clearKind, submitText, text]);

    // Llegar desde una tarjeta del instructivo tiene que dejar el cursor donde se escribe. La Barra
    // Total está ARRIBA de la guía que se clickeó, así que además hay que traerla a la vista: sin
    // el scroll el clic parece no haber hecho nada (el foco se lo lleva un campo que quedó fuera de
    // pantalla). El `preventScroll` evita el salto seco del navegador y deja mandar al suave.
    useEffect(() => {
        if (!focusToken) return;
        const el = areaRef.current;
        if (!el) return;
        if (el.scrollIntoView) el.scrollIntoView({block: 'center', behavior: 'smooth'});
        el.focus({preventScroll: true});
    }, [focusToken]);

    // Al cerrar la ÚLTIMA subida el foco vuelve al campo: el usuario está por seguir escribiendo,
    // y el botón deshabilitado por el que pasó ya no lo tiene.
    useEffect(() => {
        if (wasSendingRef.current && !sendingText && areaRef.current && !recActive) {
            areaRef.current.focus();
        }
        wasSendingRef.current = sendingText;
    }, [recActive, sendingText]);

    const onDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setDragging(false);
        clearKindIfEntered(ingestDrop(e.dataTransfer));
    }, [clearKindIfEntered, ingestDrop]);

    // Pegar una captura (la tabla de objetivos, un Ads Manager): es el gesto más natural para una
    // imagen, y una imagen ya entra. El texto pegado sigue su camino normal (`change`).
    const onPaste = useCallback((e) => {
        const items = e.clipboardData && e.clipboardData.items;
        if (!items) return;
        const images = [...items]
            .filter(item => item.kind === 'file' && String(item.type || '').startsWith('image/'))
            .map(item => item.getAsFile())
            .filter(Boolean)
            .map(pastedImageFile);
        if (!images.length) return;
        e.preventDefault();
        clearKindIfEntered(ingestFiles(images, undefined,
            {title: t('ia_booster.omni.pasted_image', {stamp: pasteStamp()})}));
    }, [clearKindIfEntered, ingestFiles, t]);

    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

    const renderUpload = (entry) => {
        const uploading = entry.status === UPLOAD_STATUS.UPLOADING;
        const indeterminate = entry.progress === null;
        const percent = indeterminate ? null : Math.round(entry.progress * 100);
        let stateLabel = t('ia_booster.omni.sending');
        if (!indeterminate) {
            stateLabel = percent >= 100
                ? t('ia_booster.omni.saving')
                : t('ia_booster.omni.uploading', {percent});
        }
        return (
            <li key={entry.id}
                className={`iab-omni-up ${uploading ? '' : '-failed'} ${indeterminate ? '-indeterminate' : ''}`}>
                <span className="iab-omni-up-ic"><FileKindIcon kind={entry.kind}/></span>
                <span className="iab-omni-up-name">{entry.name}</span>
                {uploading ? (
                    <>
                        <span className="iab-omni-up-state">{stateLabel}</span>
                        <span className="iab-omni-up-bar" role="progressbar"
                              aria-label={entry.name}
                              aria-valuemin={0} aria-valuemax={100}
                              aria-valuenow={indeterminate ? undefined : percent}>
                            <i style={indeterminate ? undefined : {width: `${percent}%`}}/>
                        </span>
                        {entry.mode === INGEST_MODE.UPLOAD && cancelUpload && (
                            <button type="button" className="iab-omni-up-btn"
                                    onClick={() => cancelUpload(entry.id)}>
                                {t('ia_booster.omni.upload_cancel')}
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <span className="iab-omni-up-err" role="alert">
                            {t(entry.reasonKey, entry.reasonParams || escape)}
                        </span>
                        {/* Sin botón cuando reintentar no puede cambiar nada: el formato que no
                            entra, el archivo repetido, o el kill-switch apagado (que viaja como
                            503 pero es permanente). Ofrecerlo ahí manda al usuario a repetir el
                            mismo gesto para el mismo resultado. */}
                        {entry.retryable !== false && (
                            <button type="button" className="iab-omni-up-btn"
                                    onClick={() => (entry.retry ? entry.retry() : ingestFile(entry.file))}>
                                {t('ia_booster.omni.upload_retry')}
                            </button>
                        )}
                        {dismissUpload && (
                            <button type="button" className="iab-omni-up-btn"
                                    onClick={() => dismissUpload(entry.id)}>
                                {t('ia_booster.omni.upload_dismiss')}
                            </button>
                        )}
                    </>
                )}
            </li>
        );
    };

    const renderRecording = () => (
        <div className="iab-omni-rec" role="group" aria-label={t('ia_booster.omni.record')}>
            <span className="iab-omni-rec-dot" aria-hidden="true"/>
            {/* El medidor sólo tiene sentido con el micrófono ya abierto: durante el permiso no hay
                señal que mostrar, y unas barras quietas ahí se leerían como "no te escucho". */}
            {isRecording && (
                <AudioLevelMeter meterRef={recorder.meterRef} label={t('ia_booster.omni.rec_level')}/>
            )}
            <span role="timer" className={`iab-omni-rec-timer ${recorder.isNearLimit ? '-near' : ''}`}>
                {formatElapsedShort(recorder.elapsedMs)}
            </span>
            <span className="iab-omni-rec-txt">
                {isRequesting ? t('ia_booster.omni.rec_requesting') : t('ia_booster.omni.rec_live')}
            </span>
            <button type="button" className="iab-omni-rec-cancel" onClick={cancelRecording}>
                {t('ia_booster.omni.rec_cancel')}
            </button>
            <button ref={stopRef} type="button" className="iab-omni-submit" disabled={!isRecording}
                    onClick={recorder.stop}>
                {t('ia_booster.omni.rec_stop')}
            </button>

        </div>
    );

    /**
     * La nota grabada, para escucharla antes de mandarla.
     *
     * El aviso de silencio NO bloquea el envío: el umbral se calibra sobre el nivel de entrada y un
     * micrófono con poca ganancia puede haber grabado voz real por debajo. Avisa y deja decidir,
     * con el reproductor ahí mismo para verificarlo en dos segundos.
     */
    const renderReview = () => {
        const {audio, reason} = pendingAudio;
        const silent = audio.hadSound === false;
        const stoppedAlone = reason && reason !== STOP_REASON_USER;
        return (
            <div className="iab-omni-rec -review" role="group" aria-label={t('ia_booster.omni.record')}>
                <div className="iab-omni-rev">
                    <div className="iab-omni-rev-head">
                        <span className="iab-omni-rev-title">
                            <Microphone size={15} aria-hidden="true"/>
                            {t('ia_booster.omni.audio_title', {
                                time: formatElapsedShort(audio.durationSeconds * 1000),
                            })}
                        </span>
                        {stoppedAlone && (
                            <span className="iab-omni-rev-note">
                                {t('ia_booster.omni.rec_paused', {
                                    time: formatElapsedShort(audio.durationSeconds * 1000),
                                })}
                                {reason === STOP_REASON_LIMIT && (
                                    <> {t('ia_booster.omni.rec_limit',
                                        {minutes: Math.round(MEMORY_AUDIO_MAX_SECONDS / 60)})}</>
                                )}
                            </span>
                        )}
                    </div>

                    <AudioNotePlayer
                        src={audio.previewUrl}
                        durationSeconds={audio.durationSeconds}
                        label={t('ia_booster.omni.rec_listen')}
                    />

                    {silent && (
                        <p className="iab-omni-rev-warn" role="alert">
                            <Warning size={15} aria-hidden="true"/>
                            {t('ia_booster.omni.rec_silent')}
                        </p>
                    )}
                </div>

                <div className="iab-omni-rev-actions">
                    <button type="button" className="iab-omni-rec-cancel" onClick={discardPending}>
                        {t('ia_booster.omni.rec_discard')}
                    </button>
                    <button type="button" className="iab-omni-rec-cancel" onClick={redoPending}>
                        <ArrowClockwise size={14} aria-hidden="true"/>
                        {t('ia_booster.omni.rec_redo')}
                    </button>
                    <button ref={sendRef} type="button" className="iab-omni-submit" onClick={sendPending}>
                        {t('ia_booster.omni.rec_send')}
                    </button>
                </div>
            </div>
        );
    };

    const renderControls = () => (
        <>
            {/* Sin `title=""` nativo: el design system lo prohíbe. `aria-label` para el lector
                de pantalla y `.iab-tip` —la versión oscura del tooltip, ver el SCSS— para el
                que ve el ícono y no sabe qué hace. */}
            <button
                type="button"
                className="iab-omni-plus"
                onClick={() => fileRef.current && fileRef.current.click()}
                aria-label={t('ia_booster.omni.pick_file')}
            >
                <Plus size={18} weight="bold"/>
                <span className="iab-tip">{t('ia_booster.omni.pick_file')}</span>
            </button>

            <textarea
                ref={areaRef}
                rows={1}
                className="iab-omni-input"
                value={text}
                disabled={disabled || sendingText}
                // El placeholder cambia con el tipo elegido; el `aria-label` NO, para que el campo
                // no cambie de nombre bajo el cursor de quien navega con teclado o lector.
                placeholder={t(kindPlaceholderKey(kind))}
                onChange={(e) => { setText(e.target.value); autoGrow(); }}
                onPaste={onPaste}
                // Enter manda; Shift+Enter hace salto de línea. Pegar una transcripción no
                // pasa por acá (el `paste` dispara `change`), así que sus saltos se conservan.
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                aria-label={t('ia_booster.omni.placeholder')}
            />

            {/* Micrófono, clip y Drive — en ese orden, que es el del mockup. El clip estaba primero
                y no cambia lo que hace ninguno, pero el orden de una fila de íconos es de las cosas
                que se comparan de un vistazo contra la referencia. */}
            {recorder.supported && (
                <button
                    ref={micRef}
                    type="button"
                    className="iab-omni-action"
                    disabled={disabled || sendingText}
                    onClick={() => { discardPending(); recorder.start(); }}
                    aria-label={t('ia_booster.omni.record')}
                >
                    <Microphone size={17}/>
                    <span className="iab-tip">{t('ia_booster.omni.record')}</span>
                </button>
            )}

            <button
                type="button"
                className="iab-omni-action"
                onClick={() => fileRef.current && fileRef.current.click()}
                aria-label={t('ia_booster.omni.attach')}
            >
                <Paperclip size={17}/>
                <span className="iab-tip">{t('ia_booster.omni.attach')}</span>
            </button>

            {/* Drive es la única fuente automática que existe hoy. Con el panel de Fuentes a
                mano se abre AHÍ (y con el Picker ya abierto), sin navegar: la ruta vieja
                redirige de vuelta a esta página pero remontándola entera. */}
            {drawer.available ? (
                <button
                    type="button"
                    className="iab-omni-action"
                    onClick={() => drawer.openSources({connect: true})}
                    aria-label={t('ia_booster.omni.from_drive')}
                >
                    <GoogleDriveLogo size={17}/>
                    <span className="iab-tip">{t('ia_booster.omni.from_drive')}</span>
                </button>
            ) : (
                <Link
                    className="iab-omni-action"
                    to={`/client/${clientId}/memory/sources?connect=drive`}
                    aria-label={t('ia_booster.omni.from_drive')}
                >
                    <GoogleDriveLogo size={17}/>
                    <span className="iab-tip">{t('ia_booster.omni.from_drive')}</span>
                </Link>
            )}

            <button
                type="button"
                className="iab-omni-submit"
                onClick={send}
                disabled={disabled || sendingText || !text.trim()}
            >
                {sendingText ? t('ia_booster.omni.sending') : t('ia_booster.omni.submit')}
            </button>
        </>
    );

    let boxContent = renderControls();
    if (recActive) boxContent = renderRecording();
    else if (pendingAudio) boxContent = renderReview();

    return (
        <div className={`iab-omni ${dragging ? '-dragging' : ''}`}>
            <div
                className={`iab-omnibox ${dragging ? '-dragging' : ''} ${multiline ? '-multiline' : ''} ${recActive ? '-recording' : ''}`}
                {...anchorAttr('booster_feed_omnibox')}
                onDragEnter={(e) => { stop(e); dragDepthRef.current += 1; setDragging(true); }}
                onDragOver={stop}
                onDragLeave={(e) => {
                    stop(e);
                    dragDepthRef.current -= 1;
                    if (dragDepthRef.current <= 0) { dragDepthRef.current = 0; setDragging(false); }
                }}
                onDrop={onDrop}
            >
                {boxContent}

                <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept={INGEST_ACCEPT}
                    className="cmem-file-input"
                    onChange={(e) => {
                        if (e.target.files && e.target.files.length) {
                            clearKindIfEntered(ingestFiles([...e.target.files]));
                        }
                        // Sin esto, elegir DOS VECES el mismo archivo no dispara `change`.
                        e.target.value = '';
                    }}
                    tabIndex={-1}
                    aria-hidden="true"
                />
            </div>

            {(uploads.length > 0 || recorder.errorKey) && (
                <ul className="iab-omni-uploads">
                    {recorder.errorKey && (
                        <li className="iab-omni-up -failed">
                            <span className="iab-omni-up-ic"><Microphone size={15} aria-hidden="true"/></span>
                            <span className="iab-omni-up-err" role="alert">{t(recorder.errorKey)}</span>
                        </li>
                    )}
                    {uploads.map(renderUpload)}
                </ul>
            )}

            {/* Región de anuncios: sólo EVENTOS de la grabación (empezó, queda poco, se detuvo).
                El timer va aparte y SIN aria-live: un anuncio por segundo es tortura. */}
            <span className="iab-sr-only" aria-live="polite">
                {isRecording && !recorder.isNearLimit && t('ia_booster.omni.rec_live')}
                {isRecording && recorder.isNearLimit && t('ia_booster.omni.rec_near_limit')}
                {pendingAudio && t('ia_booster.omni.rec_ready_announce', {
                    time: formatElapsedShort(pendingAudio.audio.durationSeconds * 1000),
                })}
                {pendingAudio && pendingAudio.audio.hadSound === false
                    && ` ${t('ia_booster.omni.rec_silent')}`}
            </span>

        </div>
    );
}

export default BoosterOmnibar;
